import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { BrandLogo } from '../../components/BrandLogo';
import { AddGroupCard } from './components/AddGroupCard';
import { GroupCard } from './components/GroupCard';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { selectUser } from '../auth/authSlice';
import { fpColors } from '../../styles/theme';
import { perf } from '../../utils/logger';
import { NotificationBell } from '../notifications/components/NotificationBell';
// FE #124.5 — Top-bar 🕒 clock-rewind icon (always visible).
import { ScanHistoryButton } from '../scans/components/ScanHistoryButton';
import {
  loadGroups,
  selectGroups,
  selectGroupsHasLoaded,
  selectGroupsLoading,
  selectGroupsError,
  clearGroupsError,
} from './groupsSlice';
// PR3 — Share-row / borrow-request / custody surfaces moved to the
// new ShareFlow tab (PR4). CrewHub is people-only now: crews, family,
// teams, and the create-crew CTA. Notifications live in the bell.
import type {
  GroupKind,
  GroupListItem,
} from '../../api/types';

/**
 * CrewHubScreen — Sprint 1 (Chunk 8) rebuild of the legacy
 * SharingScreen. Single landing surface for the Share tab.
 *
 * Renders crews + family + team groups grouped by `kind` with a
 * Personal entry pinned to the bottom. Auto-synthesizes a Personal
 * tile until backend Chunk 9 lands the per-user PERSONAL group
 * backfill (so users always see "their corner" even with no other
 * groups yet).
 *
 * BDD scenarios + ASCII wireframes documented in #28.
 */

// BE-200 — Group kinds renamed (CREW → TEAM, old TEAM → FRIENDS,
// new NEIGHBOURS added; PERSONAL stays as the hidden own-bucket).
// The hub renders one section per non-PERSONAL kind. CREW is kept
// in the record only as a defensive fallback for any legacy cached
// rows that may still come back pre-migration.
const SECTION_LABELS: Record<GroupKind, string> = {
  TEAM: 'Teams',
  FAMILY: 'Family',
  FRIENDS: 'Friends',
  NEIGHBOURS: 'Neighbours',
  PERSONAL: 'Personal',
  CREW: 'Teams', // legacy fallback — bucket merges visually into TEAM
};
const SECTION_ORDER: GroupKind[] = [
  'TEAM',
  'FAMILY',
  'FRIENDS',
  'NEIGHBOURS',
  'PERSONAL',
];

export const CrewHubScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const groups = useAppSelector(selectGroups);
  const hasLoaded = useAppSelector(selectGroupsHasLoaded);
  const loading = useAppSelector(selectGroupsLoading);
  const error = useAppSelector(selectGroupsError);

  // Sprint 8 / FE-130 — replaces both legacy banners (PendingInvitesBanner
  // + pendingBorrowCount banner) with a unified notification-backed
  // "Needs your attention" + "Recent activity" pair. Same data the bell
  // PR3 — CrewHub no longer reads notifications, shares, or
  // borrow_requests. Those live in the bell + ShareFlow tab now.

  // Initial fetch — happens on every mount; the slice's hasLoaded flag
  // gates the *spinner*, not the fetch (so a stale-data scenario still
  // refreshes when the user re-opens the tab).
  //
  // PR32 — also clear any stale error string from sibling flows
  // (e.g. acceptGroupInvite.rejected wrote to `state.error` which
  // this screen reads). Without this, accepting an already-redeemed
  // invite + then opening Groups renders "Couldn't load your groups
  // — Invite not found or already redeemed" even though groups
  // themselves loaded fine.
  // FE-251 — instrument mount so we can spot the intermittent hang
  // ("opens sometimes, hangs sometimes"). Logs:
  //   [PERF] mark CrewHub:mount        — effect fired
  //   [PERF] start groupsSlice:loadGroups — dispatch begins
  //   [PERF] end   groupsSlice:loadGroups (Xms) — thunk fulfilled
  // If we see mark but never see end, the thunk is stuck — likely a
  // network hang OR a queued action behind a long-running effect.
  useEffect(() => {
    perf.mark('CrewHub:mount');
    dispatch(clearGroupsError());
    dispatch(loadGroups());
  }, [dispatch]);

  const initials = (user?.full_name || 'U')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

  const handleRefresh = useCallback(() => {
    dispatch(loadGroups({ refresh: true }));
  }, [dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(clearGroupsError());
    dispatch(loadGroups({ refresh: true }));
  }, [dispatch]);

  const goCreate = useCallback(() => navigation.navigate('CreateGroup'), [
    navigation,
  ]);
  const goManage = useCallback(
    (groupId: number) =>
      navigation.navigate('GroupDetail', { groupId: String(groupId) }),
    [navigation],
  );

  // ── Group-by-kind + Personal synthesis ────────────────────────────
  const sectioned = useMemo(() => {
    // BE-200 — every wire kind has its own bucket. Cached responses
    // returning the legacy 'CREW' value are silently merged into
    // the TEAM bucket so they render under the right section.
    const byKind: Record<GroupKind, GroupListItem[]> = {
      TEAM: [],
      FAMILY: [],
      FRIENDS: [],
      NEIGHBOURS: [],
      PERSONAL: [],
      CREW: [], // legacy — never read; defensive landing zone
    };
    for (const g of groups) {
      if (g.archived_at) continue;
      // Map legacy 'CREW' into the TEAM bucket. Anything unrecognized
      // (forward-compat for future enum values) drops into TEAM too
      // so the user still sees the row.
      const bucket: GroupKind =
        g.kind === 'CREW'
          ? 'TEAM'
          : byKind[g.kind as GroupKind] != null
            ? (g.kind as GroupKind)
            : 'TEAM';
      byKind[bucket].push(g);
    }
    // Synthesize a Personal entry if the backend hasn't backfilled one
    // yet (Chunk 9 lands per-user PERSONAL via migration). Until then
    // every user always sees their own corner.
    if (byKind.PERSONAL.length === 0 && user?.id != null) {
      byKind.PERSONAL.push({
        id: -1, // synthetic — never sent to server
        name: 'Personal',
        kind: 'PERSONAL',
        owner_user_id: user.id,
        description: 'Just you',
        auto_approve_handoff: false,
        archived_at: null,
        created_at: '',
        updated_at: '',
        my_role: 'OWNER',
        member_count: 1,
        item_count: 0,
      });
    }
    return byKind;
  }, [groups, user?.id]);

  // BE-200 — sum every non-PERSONAL bucket. Legacy CREW rows are
  // already remapped to TEAM above, so this counts every visible
  // section the user might land on.
  const totalNonPersonal =
    sectioned.TEAM.length +
    sectioned.FAMILY.length +
    sectioned.FRIENDS.length +
    sectioned.NEIGHBOURS.length;
  const isEmpty = totalNonPersonal === 0;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <BrandLogo variant="horizontal" height={24} tone="color" />
          <View style={styles.topBarRight}>
            <Pressable
              onPress={() => navigation.navigate('CrossGroupSearch')}
              style={styles.iconBtn}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Search across all your circles"
              testID="crewhub-search-btn"
            >
              <MaterialIcons
                name="search"
                size={22}
                color={fpColors.onSurface}
              />
            </Pressable>
            {/* FE #124.5 — Scan history shortcut. */}
            <ScanHistoryButton
              onPress={() => navigation.navigate('ScanHistory')}
            />
            <NotificationBell
              onPress={() => navigation.navigate('Notifications')}
            />
            <Pressable
              onPress={() => navigation.navigate('Profile')}
              style={styles.avatar}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Profile"
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </Pressable>
          </View>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={loading.list && hasLoaded}
            onRefresh={handleRefresh}
            tintColor={fpColors.primary}
          />
        }
      >
        <View style={styles.hero} accessibilityRole="header">
          <Text style={styles.h1}>My Circles</Text>
          <Text style={styles.subtitle}>
            The people you share inventory with — family, friends,
            teams, and neighbours. Tap a circle to manage its members and
            items.
          </Text>
        </View>

        {/* Sprint 8 follow-up (PR3) — Crews tab is now people-only.
            The previous CrewHub blended:
              - "Needs your attention" notification queue
              - Available-to-claim / Shares-I-sent / Currently-with-me
              - Out-on-loan
            All of those have migrated:
              - Action queue → notification bell (one inbox surface)
              - Share/borrow rows → new ShareFlow tab (PR4)
              - Out-on-loan → ShareFlow's "Out from me" union
            This screen now strictly answers "who am I sharing with?"
            and is the entry point to crew creation + management. */}

        {/* Loading state — only for first paint, before initial data */}
        {!hasLoaded && loading.list && (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color={fpColors.primary} />
            <Text style={styles.loadingText}>Loading your circles…</Text>
          </View>
        )}

        {/* Error banner */}
        {hasLoaded && error && (
          <View style={styles.errorBanner}>
            <MaterialIcons
              name="error-outline"
              size={20}
              color={fpColors.error}
            />
            <View style={{ flex: 1 }}>
              <Text style={styles.errorTitle}>Couldn&apos;t load your circles</Text>
              <Text style={styles.errorMeta}>{error}</Text>
            </View>
            <Pressable
              onPress={handleRetry}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Retry loading circles"
              style={styles.retryBtn}
            >
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          </View>
        )}

        {/* Primary CTA — large hero variant when empty, compact when populated */}
        {hasLoaded && !error && (
          <>
            {isEmpty ? (
              <Pressable
                onPress={goCreate}
                style={({ pressed }) => [
                  styles.heroCta,
                  pressed && { opacity: 0.9 },
                ]}
                accessibilityRole="button"
                accessibilityLabel="Create your first circle"
              >
                <View style={styles.heroCtaIcon}>
                  <MaterialIcons name="group-add" size={22} color={fpColors.onPrimary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.heroCtaTitle}>Create your first circle</Text>
                  <Text style={styles.heroCtaMeta}>
                    Pick a name, invite people, share kit
                  </Text>
                </View>
                <MaterialIcons
                  name="arrow-forward"
                  size={20}
                  color={fpColors.onPrimary}
                />
              </Pressable>
            ) : (
              <AddGroupCard onPress={goCreate} />
            )}
          </>
        )}

        {/* Sections — each kind in declared order, only render when non-empty.
            PR8 — collapsible + top-3 + show-more pattern. See
            <CrewSection /> for the per-section behavior.

            Sort within each section: most-recently-updated first (server
            stamps `updated_at` on any membership / inventory change),
            fallback to item_count desc, fallback to alphabetical. */}
        {hasLoaded &&
          !error &&
          SECTION_ORDER.map((kind) => {
            const items = sortGroups(sectioned[kind]);
            if (items.length === 0) return null;
            return (
              <CrewSection
                key={kind}
                kind={kind}
                title={SECTION_LABELS[kind]}
                items={items}
                onItemPress={(g) =>
                  g.id > 0 ? goManage(g.id) : navigation.navigate('Items')
                }
                onManagePress={(g) =>
                  g.id > 0 ? () => goManage(g.id) : undefined
                }
                onCreate={kind === 'CREW' ? goCreate : undefined}
              />
            );
          })}

        {/* Recent activity + borrow-requests link removed in PR3 —
            the notification bell is the single inbox surface; the
            Share tab is the single share/borrow surface. */}
      </ScrollView>
    </View>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * CrewSection — PR8 collapsible-section primitive.
 *
 * Per the user pain ("too many crews, can't reach Family / Teams"),
 * each section now:
 *   - shows the section header with chevron + count badge
 *   - the WHOLE header is tappable to collapse the section to nothing
 *     (state persists in localStorage so power-users keep their layout)
 *   - when expanded, shows the top 3 groups
 *   - if there are more than 3, shows a "Show N more →" affordance
 *     that expands the rest IN-PLACE (no separate screen)
 *   - the CREW section gets the `[+ New crew]` button in its header
 *
 * Sort order inside the section is deterministic — see `sortGroups`.
 * Default state for every section is EXPANDED (no surprises on first
 * open). User opts into collapse.
 * ──────────────────────────────────────────────────────────────────── */

interface CrewSectionProps {
  kind: GroupKind;
  title: string;
  items: GroupListItem[];
  onItemPress: (g: GroupListItem) => void;
  onManagePress: (g: GroupListItem) => (() => void) | undefined;
  /** When defined, renders the `[+ New crew]` button on the right of
   *  the header. Only passed for the CREW section. */
  onCreate?: () => void;
}

const VISIBLE_COUNT = 3;

const CrewSection: React.FC<CrewSectionProps> = ({
  kind,
  title,
  items,
  onItemPress,
  onManagePress,
  onCreate,
}) => {
  const total = items.length;
  const [collapsed, setCollapsed] = useCollapsedState(kind);
  // "Show N more →" inline expansion — distinct from `collapsed`,
  // which hides the entire section. This just reveals items 4..N
  // without leaving the page. NOT persisted (user expectation: each
  // visit, the page opens to the "top 3 only" condensed view).
  const [showAll, setShowAll] = useState(false);

  const visible = useMemo(() => {
    if (collapsed) return [];
    if (showAll) return items;
    return items.slice(0, VISIBLE_COUNT);
  }, [items, showAll, collapsed]);

  const hiddenCount = total - VISIBLE_COUNT;

  return (
    <View style={styles.section}>
      {/* Sticky-feeling section header. Tappable across most of its
          width to flip collapsed state; the `+ New crew` button (if
          present) is its own pressable that stops propagation. */}
      <Pressable
        onPress={() => setCollapsed(!collapsed)}
        style={({ pressed }) => [
          styles.sectionHeader,
          pressed && { opacity: 0.85 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={`${title}, ${total} ${total === 1 ? 'circle' : 'circles'}, ${collapsed ? 'collapsed' : 'expanded'}. Tap to ${collapsed ? 'expand' : 'collapse'}.`}
        testID={`crew-section-toggle-${kind.toLowerCase()}`}
      >
        <View style={styles.sectionHeaderLeft}>
          <Text style={styles.sectionLabel}>{title}</Text>
          <View style={styles.sectionCountPill}>
            <Text style={styles.sectionCountText}>{total}</Text>
          </View>
        </View>
        <View style={styles.sectionHeaderRight}>
          {onCreate && (
            <Pressable
              onPress={(e) => {
                e?.stopPropagation?.();
                onCreate();
              }}
              style={({ pressed }) => [
                styles.sectionAction,
                pressed && { opacity: 0.7 },
              ]}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Create a new circle"
              testID="crew-section-add"
            >
              <MaterialIcons name="add" size={14} color={fpColors.primary} />
              <Text style={styles.sectionActionText}>New circle</Text>
            </Pressable>
          )}
          <MaterialIcons
            name={collapsed ? 'chevron-right' : 'expand-more'}
            size={20}
            color={fpColors.onSurfaceVariant}
          />
        </View>
      </Pressable>

      {!collapsed && (
        <View style={styles.groupList}>
          {visible.map((g, idx) => (
            <GroupCard
              key={g.id}
              group={g}
              featured={kind !== 'PERSONAL' && idx === 0}
              onPress={() => onItemPress(g)}
              onManagePress={onManagePress(g)}
            />
          ))}
          {!showAll && hiddenCount > 0 && (
            <Pressable
              onPress={() => setShowAll(true)}
              style={({ pressed }) => [
                styles.showMore,
                pressed && { opacity: 0.7 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Show all ${total} ${title.toLowerCase()}`}
              testID={`crew-section-show-more-${kind.toLowerCase()}`}
            >
              <Text style={styles.showMoreText}>
                Show {hiddenCount} more →
              </Text>
            </Pressable>
          )}
          {showAll && hiddenCount > 0 && (
            <Pressable
              onPress={() => setShowAll(false)}
              style={({ pressed }) => [
                styles.showMore,
                pressed && { opacity: 0.7 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Show fewer"
              testID={`crew-section-show-less-${kind.toLowerCase()}`}
            >
              <Text style={styles.showMoreText}>Show fewer ↑</Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Hooks + helpers
 * ────────────────────────────────────────────────────────────────── */

const COLLAPSE_STORAGE_PREFIX = 'crewhub:collapsed:';

/** Per-kind collapse state persisted in localStorage. Reads on mount
 *  (sync — no flicker); writes on toggle. Falls back to false (expanded)
 *  if localStorage is unavailable (React Native, SSR, private mode). */
function useCollapsedState(
  kind: GroupKind,
): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;
      return window.localStorage.getItem(COLLAPSE_STORAGE_PREFIX + kind) === '1';
    } catch {
      return false;
    }
  });

  const update = useCallback(
    (next: boolean) => {
      setCollapsed(next);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          if (next) {
            window.localStorage.setItem(COLLAPSE_STORAGE_PREFIX + kind, '1');
          } else {
            window.localStorage.removeItem(COLLAPSE_STORAGE_PREFIX + kind);
          }
        }
      } catch {
        /* private mode etc. — collapse state is per-session only */
      }
    },
    [kind],
  );

  return [collapsed, update];
}

/** Sort groups within a section. Most-recently-updated first, fallback
 *  most-items, fallback alphabetical. Pure for testability + memo
 *  stability. */
function sortGroups(items: GroupListItem[]): GroupListItem[] {
  return [...items].sort((a, b) => {
    // 1. Most recent activity wins
    const aT = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const bT = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    if (Number.isFinite(aT) && Number.isFinite(bT) && aT !== bT) {
      return bT - aT;
    }
    // 2. Most items wins
    const aC = a.item_count ?? 0;
    const bC = b.item_count ?? 0;
    if (aC !== bC) return bC - aC;
    // 3. Alphabetical fallback
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

/* ────────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  topBarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },

  scroll: { padding: 16, paddingBottom: 32, gap: 14 },
  hero: { marginBottom: 4, gap: 4 },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },

  // Sprint 8 / FE-130 — Needs your attention + Recent activity.
  attentionSection: {
    gap: 8,
  },
  attentionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  attentionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  attentionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  countPill: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 8,
    borderRadius: 10,
    backgroundColor: fpColors.tertiaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onTertiaryContainer,
    letterSpacing: 0.3,
  },
  attentionCard: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: `${fpColors.tertiaryContainer}66`,
    borderRadius: 14,
    overflow: 'hidden',
  },
  activityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  activityCard: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    overflow: 'hidden',
  },
  viewAllText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },

  // Phase C — aggregate share/borrow section styles.
  sharedSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
    marginBottom: 6,
  },
  sharedSectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  sharedCard: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    overflow: 'hidden',
  },

  loadingWrap: {
    paddingVertical: 32,
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderWidth: 1,
    borderColor: fpColors.errorContainer,
  },
  errorTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.error,
  },
  errorMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onErrorContainer,
    marginTop: 1,
  },
  retryBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: fpColors.error,
  },
  retryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onPrimary,
  },

  // Empty-state large CTA
  heroCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 18,
    borderRadius: 14,
    backgroundColor: fpColors.primary,
  },
  heroCtaIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  heroCtaTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onPrimary,
  },
  heroCtaMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: `${fpColors.onPrimary}cc`,
    marginTop: 2,
  },

  section: { gap: 8, marginTop: 6 },
  sectionLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    letterSpacing: 0.6,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    paddingLeft: 2,
  },
  // PR8 collapsible section header — full-width tappable row that
  // flips collapse state. Houses the label + count badge + optional
  // [+ New crew] action + chevron.
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 2,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  sectionHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionCountPill: {
    minWidth: 22,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
  },
  sectionCountText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  sectionAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  sectionActionText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  // Inline "Show N more →" / "Show fewer ↑" affordance. Lives below
  // the top-3 GroupCards. Tapping flips the in-page expansion state.
  showMore: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 10,
    alignItems: 'center',
  },
  showMoreText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  groupList: { gap: 12 },

  footerLinks: { marginTop: 12 },
  footerLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
  },
  footerLinkText: {
    flex: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.primary,
  },
});

/** Backwards-compat re-export — old code paths import `SharingScreen`
 *  by name from this file. We keep the symbol stable so AppNavigator
 *  and any deep-import doesn't 404. New code should import
 *  `CrewHubScreen` directly. */
export const SharingScreen = CrewHubScreen;
